import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Tag } from 'lucide-react';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import ConfirmModal from '../components/common/ConfirmModal';
import { useEvent } from '../context/EventContext';
import { useToastContext } from '../context/ToastContext';
import { ticketAPI, eventAPI } from '../api/endpoints';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Loading from '../components/common/Loading';

const TicketingSettings = () => {
  const { selectedEvent } = useEvent();
  const toast = useToastContext();
  const queryClient = useQueryClient();
  
  const [tickets, setTickets] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [isTicketingEnabled, setIsTicketingEnabled] = useState(false);
  
  const { data: eventsData, isLoading: loadingEvents } = useQuery({
    queryKey: ['events'],
    queryFn: eventAPI.getAll,
  });

  const event = eventsData?.data?.events?.find((e: any) => e.id === selectedEvent);

  useEffect(() => {
    if (event) {
      setIsTicketingEnabled(!!event.is_ticketing_enabled);
    }
  }, [event]);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '' as number | string,
    quantity_available: '',
    is_active: true,
  });

  useEffect(() => {
    if (selectedEvent && selectedEvent !== 'new') {
      fetchTickets();
    }
  }, [selectedEvent]);

  const fetchTickets = async () => {
    try {
      const response = await ticketAPI.getAll(selectedEvent);
      setTickets(response.data.data);
    } catch (error) {
      toast.error('Failed to fetch tickets');
    } finally {
      setLoadingTickets(false);
    }
  };

  const updateEventMutation = useMutation({
    mutationFn: (data: any) => eventAPI.update(selectedEvent, data),
    onSuccess: () => {
      toast.success('Ticketing status updated!');
      queryClient.invalidateQueries({ queryKey: ['events'] });
    },
    onError: () => {
      toast.error('Failed to update event');
    }
  });

  const handleToggleTicketing = async (enabled: boolean) => {
    setIsTicketingEnabled(enabled);
    updateEventMutation.mutate({ is_ticketing_enabled: enabled });
  };

  const handleOpenModal = (ticket = null) => {
    if (ticket) {
      setFormData({
        name: ticket.name,
        description: ticket.description || '',
        price: ticket.price,
        quantity_available: ticket.quantity_available || '',
        is_active: ticket.is_active,
      });
      setSelectedTicket(ticket);
    } else {
      setFormData({
        name: '',
        description: '',
        price: '',
        quantity_available: '',
        is_active: true,
      });
      setSelectedTicket(null);
    }
    setShowModal(true);
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        price: formData.price === '' ? 0 : Number(formData.price),
        quantity_available: formData.quantity_available ? Number(formData.quantity_available) : null,
      };

      if (selectedTicket) {
        await ticketAPI.update(selectedEvent, selectedTicket.id, payload);
        toast.success('Tier updated successfully');
      } else {
        await ticketAPI.create(selectedEvent, payload);
        toast.success('Tier created successfully');
      }
      setShowModal(false);
      fetchTickets();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to save tier');
    }
  };

  const handleDeleteClick = (ticket: any) => {
    setSelectedTicket(ticket);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await ticketAPI.delete(selectedEvent, selectedTicket.id);
      toast.success('Tier deleted successfully');
      setShowDeleteModal(false);
      fetchTickets();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete tier');
      setShowDeleteModal(false);
    }
  };

  if (loadingEvents || !event) {
    return <Loading />;
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Ticketing</h1>
          <p className="text-gray-600 mt-1 text-sm sm:text-base">Manage ticket sales and tiers for your event</p>
        </div>
      </div>

      <Card>
        <Card.Content>
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-gray-900">Enable Ticketing</h3>
              <p className="text-sm text-gray-500">Allow attendees to select and purchase tickets during registration.</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={isTicketingEnabled}
                onChange={(e) => handleToggleTicketing(e.target.checked)}
                disabled={updateEventMutation.isPending}
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-teal-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-600"></div>
            </label>
          </div>
        </Card.Content>
      </Card>

      {isTicketingEnabled && (
        <Card>
          <div className="flex justify-between items-center p-6 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Ticket Tiers</h3>
            <Button onClick={() => handleOpenModal()} className="gap-2">
              <Plus className="h-5 w-5" />
              Create Tier
            </Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price (GHS)</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stock Limit</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loadingTickets ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-4 text-center text-gray-500">Loading tickets...</td>
                  </tr>
                ) : tickets.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-4 text-center text-gray-500">No ticket tiers created yet.</td>
                  </tr>
                ) : (
                  tickets.map((ticket: any) => (
                    <tr key={ticket.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <Tag className="h-4 w-4 text-gray-400" />
                          <span className="font-medium text-gray-900">{ticket.name}</span>
                        </div>
                        {ticket.description && <p className="text-xs text-gray-500 mt-1 truncate max-w-xs">{ticket.description}</p>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="font-medium">{ticket.price > 0 ? `GHS ${ticket.price}` : 'Free'}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                        {ticket.quantity_available !== null ? ticket.quantity_available : 'Unlimited'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${ticket.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                          {ticket.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => handleOpenModal(ticket)}
                            className="text-primary-600 hover:text-primary-900 bg-primary-50 p-2 rounded-lg"
                            title="Edit"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteClick(ticket)}
                            className="text-danger-600 hover:text-danger-900 bg-danger-50 p-2 rounded-lg"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-lg">
            <Card.Header>
              <h2 className="text-xl font-bold">{selectedTicket ? 'Edit Tier' : 'Create Tier'}</h2>
            </Card.Header>
            <Card.Content>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tier Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500"
                    placeholder="e.g. Regular, VIP"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description (Optional)</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500"
                    rows={3}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Price (GHS) *</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      required
                      value={formData.price === 0 ? '' : formData.price}
                      onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [-moz-appearance:textfield]"
                    />
                    <p className="text-xs text-gray-500 mt-1">Set to 0 for free tickets</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Stock Limit</label>
                    <input
                      type="number"
                      min="1"
                      value={formData.quantity_available || ''}
                      onChange={(e) => setFormData({ ...formData, quantity_available: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [-moz-appearance:textfield]"
                      placeholder="Leave blank for unlimited"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-4">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                  />
                  <label htmlFor="is_active" className="text-sm font-medium text-gray-700">
                    Active (visible on registration form)
                  </label>
                </div>
                
                <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-gray-200">
                  <Button type="button" variant="secondary" onClick={() => setShowModal(false)}>
                    Cancel
                  </Button>
                  <Button type="submit">
                    {selectedTicket ? 'Save Changes' : 'Create Tier'}
                  </Button>
                </div>
              </form>
            </Card.Content>
          </Card>
        </div>
      )}

      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        title="Delete Tier"
        message="Are you sure you want to delete this ticket tier? This action cannot be undone."
      />
    </div>
  );
};

export default TicketingSettings;
